import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const location = useLocation();
  const resetSuccess = (location.state as { resetSuccess?: boolean } | null)?.resetSuccess;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Send MFA credentials whenever present — do not wait for mfaRequired from a prior
    // failed response, so users can sign in in one step (password + TOTP) and the field
    // is always visible below.
    const result = await login(
      email,
      password,
      !useBackupCode && mfaCode.trim() ? mfaCode : undefined,
      useBackupCode && backupCode.trim() ? backupCode : undefined,
    );

    if (!result.success && result.mfaRequired) {
      setMfaRequired(true);
      setMfaEnrollmentRequired(Boolean(result.mfaEnrollmentRequired));
      // MFA challenge: avoid duplicating a scary red alert; info banner + code fields below suffice.
      setError(
        result.mfaEnrollmentRequired
          ? result.error || 'MFA enrollment is required for your organization.'
          : null,
      );
    } else if (!result.success) {
      setMfaRequired(false);
      setMfaEnrollmentRequired(false);
      setMfaCode('');
      setBackupCode('');
      setUseBackupCode(false);
      setError(result.error || 'Login failed');
    } else {
      setMfaRequired(false);
      setMfaEnrollmentRequired(false);
      setMfaCode('');
      setBackupCode('');
      setUseBackupCode(false);
    }
    setIsLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 420,
          width: '100%',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          background: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
                src="/images/migratr.ai L.png"
                alt="migratr"
              sx={{
                width: 240,
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {resetSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Your password was updated. Sign in with your new password.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              sx={{ mb: 2 }}
              autoComplete="email"
              autoFocus
            />

            <TextField
              fullWidth
              label="Password"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              sx={{ mb: 3 }}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {!mfaRequired && (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || !email || !password}
                sx={{
                  mt: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            )}

            {mfaRequired && (
              <Box sx={{ mt: 2, animation: 'fadeIn 0.5s ease-in-out' }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  {mfaEnrollmentRequired
                    ? 'MFA enrollment is required for your organization. Contact your administrator to complete enrollment.'
                    : 'Multi-factor authentication is enabled for this account.'}
                </Alert>

                {!mfaEnrollmentRequired && !useBackupCode && (
                  <TextField
                    fullWidth
                    label="Authenticator code"
                    variant="outlined"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    disabled={isLoading}
                    sx={{ mb: 2 }}
                    autoComplete="one-time-code"
                    autoFocus
                    helperText="Enter the 6-digit code from your authenticator app."
                  />
                )}

                {!mfaEnrollmentRequired && useBackupCode && (
                  <TextField
                    fullWidth
                    label="Backup code"
                    variant="outlined"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                    disabled={isLoading}
                    sx={{ mb: 2 }}
                    autoFocus
                    helperText="Enter one of your 8-digit backup codes."
                  />
                )}

                {!mfaEnrollmentRequired && (
                  <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => {
                        setUseBackupCode((prev) => !prev);
                        setMfaCode('');
                        setBackupCode('');
                      }}
                      disabled={isLoading}
                    >
                      {useBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
                    </Link>
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={
                    isLoading ||
                    (mfaRequired && !mfaEnrollmentRequired && !useBackupCode && !mfaCode.trim()) ||
                    (mfaRequired && !mfaEnrollmentRequired && useBackupCode && !backupCode.trim())
                  }
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Verify & Sign In'
                  )}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setMfaRequired(false);
                    setMfaCode('');
                    setBackupCode('');
                    setUseBackupCode(false);
                    setError(null);
                  }}
                  sx={{ mt: 1 }}
                  disabled={isLoading}
                >
                  Back to Login
                </Button>
              </Box>
            )}

            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
              <Link component={RouterLink} to="/forgot-password" underline="hover">
                Forgot password?
              </Link>
            </Typography>
          </form>

          {/* Footer */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            © 2026 Aventior. All rights reserved.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
